import React, { Component } from "react";
import { PageHeader, Row, Col, Card, Statistic, Spin, Empty, Table, Typography, message } from "antd";
import { FileTextOutlined, ClockCircleOutlined, FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import { Form, FormItem, ButtonItem, RangePickerItem } from "../../../../Components/Items";
import TabbedDynamicTable from "../../../../Components/TabbedDynamicTable";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import axios from "axios";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import * as XLSX from "xlsx";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import moment from "moment";
import "moment/locale/es";
import reporteSoportePdf from "../../../../Pdf/reporteSoportePdf";
import { FASES_COLOR_HEX, SOPORTE_TEMA, textoSobreFase } from "../Chat/fasesSoporte";
import {
  ESTILOS_REPORTE,
  NIVO_TEMA_SOPORTE,
  CARD_KPI_STYLE,
  CardGrafica,
  ACENTOS,
} from "./estilosReporteSoporte";
import CardPuntosHistoria from "./CardPuntosHistoria";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
moment.locale("es");

const { Text } = Typography;

// "Atendido" es resuelto + subido, así que la serie va en verde.
const COLOR_ATENDIDOS = FASES_COLOR_HEX.resuelto;

// Los reportes se resuelven casi siempre el mismo día: en días saldrían todos
// como "0.4", así que se muestran en horas hasta las 24.
const formatearDuracion = (horas) => {
  if (horas === null || horas === undefined) return "—";
  if (horas < 1) return `${Math.round(horas * 60)} min`;
  if (horas < 24) return `${horas} h`;
  return `${Math.round((horas / 24) * 10) / 10} d`;
};

const formatearPuntos = (puntos) => (puntos === null || puntos === undefined ? "—" : String(puntos));

const VACIO = {
  kpis: {},
  porReportero: [],
  porFase: [],
  porSemana: [],
  porEmpresa: [],
  corporativo: {},
  empresa: null,
};

class ReporteSoporteEmpresa extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    datos: VACIO,
    periodo: [moment().startOf("year"), moment()],
  };

  componentDidMount() {
    this.consultar({ periodo: this.state.periodo });
  }

  consultar = async (values) => {
    const [inicio, fin] = values?.periodo || [];
    this.setState({ loading: true, periodo: values?.periodo || [] });
    try {
      const res = await axios.post(
        "soporte/metricasSoporteEmpresa",
        {
          fechaInicio: inicio ? inicio.startOf("day").toISOString() : null,
          fechaFin: fin ? fin.endOf("day").toISOString() : null,
        },
        { headers: this.props.headersToken },
      );
      if (res.data.success === 2) this.setState({ datos: res.data });
      else message.info(res.data.message || "El reporte no se cargó, intenta de nuevo.");
    } catch (error) {
      message.info("Revisa tu conexión e intenta de nuevo.");
    } finally {
      this.setState({ loading: false });
    }
  };

  textoPeriodo = () => {
    const [inicio, fin] = this.state.periodo || [];
    if (!inicio || !fin) return "Histórico completo";
    return `${inicio.format("LL")} — ${fin.format("LL")}`;
  };

  textoAlcance = () => {
    const { corporativo, kpis } = this.state.datos;
    const delCorporativo = corporativo?.empresas?.length || 0;
    const reportaron = kpis?.empresasReportando || 0;
    if (delCorporativo <= 1) return "";
    return `${reportaron} de ${delCorporativo} empresas con reportes`;
  };

  filasResumen = () => {
    const { kpis } = this.state.datos;
    return [
      ["Reportes levantados", String(kpis.totalTickets ?? 0)],
      ["Empresas con reportes", String(kpis.empresasReportando ?? 0)],
      ["Abiertos hoy", String(kpis.abiertos ?? 0)],
      ["Atendidos", String(kpis.atendidos ?? 0)],
      ["Cerrados", String(kpis.rechazados ?? 0)],
      ["Tiempo promedio de atención", formatearDuracion(kpis.horasPromedioResolucion)],
      ["Tiempo mediano de atención", formatearDuracion(kpis.horasMedianaResolucion)],
      ["Reportes en la muestra de atención", `${kpis.ticketsConResolucion ?? 0} de ${kpis.totalTickets ?? 0}`],
      ["Primera respuesta de soporte (promedio)", formatearDuracion(kpis.horasPromedioPrimeraRespuesta)],
      // Complejidad que le asignó desarrollo en la escala Fibonacci de 1 a 21.
      ["Puntos de complejidad promedio (escala 1-21)", formatearPuntos(kpis.puntosPromedio)],
      ["Puntos de complejidad medianos", formatearPuntos(kpis.puntosMediana)],
      ["Reportes calificados por desarrollo", `${kpis.ticketsConPuntos ?? 0} de ${kpis.totalTickets ?? 0}`],
      ["Confirmados por ustedes", String(kpis.confirmadosPorCliente ?? 0)],
      ["Marcados como no cumplidos", String(kpis.noCumplidosPorCliente ?? 0)],
    ];
  };

  descargarExcel = () => {
    const { porReportero, porFase, porSemana, porEmpresa, corporativo } = this.state.datos;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet([
        { Indicador: "Corporativo", Valor: corporativo?.nombre || "" },
        { Indicador: "Periodo", Valor: this.textoPeriodo() },
        ...this.filasResumen().map(([Indicador, Valor]) => ({ Indicador, Valor })),
      ]),
      "Resumen",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(porFase.map((f) => ({ Estado: f.label, Reportes: f.total }))),
      "Por estado",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        porEmpresa.map((e) => ({
          Empresa: e.nombre,
          Reportes: e.total,
          Abiertos: e.abiertos,
          Atendidos: e.atendidos,
          Cerrados: e.rechazados,
        })),
      ),
      "Por empresa",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        porReportero.map((r) => ({
          Usuario: r.nombre,
          Correo: r.correo || "",
          Empresa: r.empresa || "",
          Reportes: r.total,
        })),
      ),
      "Quien reporta",
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(
        porSemana.map((s) => ({ Semana: s.semana, Inicio: s.etiqueta, Levantados: s.abiertos, Atendidos: s.atendidos })),
      ),
      "Por semana",
    );

    XLSX.writeFile(wb, `Reporte_Soporte_${moment().format("DD-MM-YYYY")}.xlsx`);
  };

  descargarPdf = () => {
    const { datos, periodo } = this.state;
    const { porReportero, porFase, porEmpresa, corporativo, empresa } = datos;
    const [inicio, fin] = periodo || [];

    const dd = reporteSoportePdf({
      empresa,
      corporativo,
      fechaInicial: inicio,
      fechaFinal: fin,
      alcance: this.textoAlcance(),
      resumen: this.filasResumen(),
      porFase,
      porEmpresa,
      porReportero,
    });

    pdfMake.createPdf(dd).download(`Reporte_Soporte_${moment().format("DD-MM-YYYY")}.pdf`);
  };

  render() {
    const { loading, datos } = this.state;
    const { kpis, porReportero, porFase, porSemana, porEmpresa, corporativo } = datos;
    const hayDatos = (kpis.totalTickets || 0) > 0;
    const variasEmpresas = porEmpresa.length > 1;

    return (
      <>
        <style>{ESTILOS_REPORTE}</style>
        <PageHeader
          title="Reporte de Soporte"
          subTitle={
            corporativo?.nombre
              ? `${corporativo.nombre} — todas tus empresas`
              : "Cómo va la atención de los reportes de tus empresas"
          }
          onBack={() => this.props.history.push("/panel/ticketsSoporte")}
          extra={
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {/* El botón vive fuera del <Form>, así que el submit se dispara por ref. */}
              <ButtonItem
                className="botonAgregar"
                type="primary"
                loading={loading}
                onClick={() => this.formRef.current?.submit()}
              >
                Consultar
              </ButtonItem>
              <ButtonItem
                className="botonAgregar"
                rojo
                icon={<FilePdfOutlined />}
                disabled={!hayDatos}
                onClick={this.descargarPdf}
              >
                PDF
              </ButtonItem>
              <ButtonItem
                className="botonAgregar"
                verde
                icon={<FileExcelOutlined />}
                disabled={!hayDatos}
                onClick={this.descargarExcel}
              >
                Excel
              </ButtonItem>
            </div>
          }
          style={{ border: "1px solid rgb(235, 237, 240)", backgroundColor: "white", marginBottom: 10 }}
        />

        <div className="rep-root">
          <Form ref={this.formRef} onFinish={this.consultar} initialValues={{ periodo: this.state.periodo }}>
            <TabbedDynamicTable
              showContentBorder={false}
              tabs={[
                {
                  key: "1",
                  label: "Periodo",
                  forceRender: true,
                  customContent: (
                    <FormItem name="periodo" margin>
                      <RangePickerItem />
                    </FormItem>
                  ),
                },
              ]}
            />
          </Form>

          <Spin spinning={loading}>
            {!hayDatos ? (
              <Empty description="No hay reportes en el periodo seleccionado" />
            ) : (
              <>
                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                  <Col xs={24} sm={12} lg={8} style={{ display: "flex" }}>
                    <Card style={{ ...CARD_KPI_STYLE, flex: 1 }}>
                      <Statistic title="Reportes levantados" value={kpis.totalTickets} prefix={<FileTextOutlined />} />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {this.textoAlcance() || "en el periodo seleccionado"}
                      </Text>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={8} style={{ display: "flex" }}>
                    <Card style={{ ...CARD_KPI_STYLE, flex: 1 }}>
                      <Statistic
                        title="Abiertos hoy"
                        value={kpis.abiertos}
                        prefix={<ClockCircleOutlined />}
                        valueStyle={{ color: SOPORTE_TEMA.primario }}
                      />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        todavía en proceso
                      </Text>
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={8} style={{ display: "flex" }}>
                    <CardPuntosHistoria
                      promedio={kpis.puntosPromedio}
                      mediana={kpis.puntosMediana}
                      ticketsConPuntos={kpis.ticketsConPuntos}
                      totalTickets={kpis.totalTickets}
                    />
                  </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={12}>
                    <CardGrafica
                      etiqueta="Estados"
                      titulo="Reportes por estado"
                      pill="Periodo"
                      accent={ACENTOS.lila}
                      alto={280}
                      delay="0.06s"
                    >
                      <ResponsivePie
                        data={porFase.map((f) => ({ id: f.label, label: f.label, value: f.total, fase: f.fase }))}
                        margin={{ top: 20, right: 20, bottom: 50, left: 20 }}
                        innerRadius={0.6}
                        padAngle={1}
                        cornerRadius={3}
                        colors={({ data }) => FASES_COLOR_HEX[data.fase] || "#d9d9d9"}
                        enableArcLinkLabels={false}
                        arcLabelsTextColor={({ data }) => textoSobreFase(data.fase)}
                        borderWidth={0}
                        activeOuterRadiusOffset={7}
                        legends={[
                          {
                            anchor: "bottom",
                            direction: "row",
                            translateY: 40,
                            itemWidth: 78,
                            itemHeight: 16,
                            symbolSize: 9,
                            symbolShape: "circle",
                            itemsSpacing: 2,
                            itemTextColor: "#64748b",
                          },
                        ]}
                        theme={NIVO_TEMA_SOPORTE}
                      />
                    </CardGrafica>
                  </Col>

                  <Col xs={24} lg={12}>
                    <CardGrafica
                      etiqueta="Tendencia"
                      titulo="Levantados vs atendidos por semana"
                      pill="Por semana"
                      alto={280}
                      delay="0.12s"
                    >
                      <ResponsiveBar
                        data={porSemana.map((s) => ({
                          semana: s.etiqueta,
                          Levantados: s.abiertos,
                          Atendidos: s.atendidos,
                        }))}
                        keys={["Levantados", "Atendidos"]}
                        indexBy="semana"
                        groupMode="grouped"
                        margin={{ top: 20, right: 110, bottom: 60, left: 40 }}
                        padding={0.25}
                        innerPadding={2}
                        colors={[SOPORTE_TEMA.primario, COLOR_ATENDIDOS]}
                        borderRadius={3}
                        axisBottom={{ tickRotation: -35 }}
                        axisLeft={{ format: (v) => (Number.isInteger(v) ? v : "") }}
                        labelSkipHeight={12}
                        enableGridX={false}
                        legends={[
                          {
                            dataFrom: "keys",
                            anchor: "bottom-right",
                            direction: "column",
                            translateX: 105,
                            itemWidth: 90,
                            itemHeight: 18,
                            symbolSize: 10,
                            symbolShape: "circle",
                            itemTextColor: "#64748b",
                          },
                        ]}
                        theme={NIVO_TEMA_SOPORTE}
                      />
                    </CardGrafica>
                  </Col>

                  {variasEmpresas && (
                    <Col xs={24}>
                      <CardGrafica
                        etiqueta="Volumen"
                        titulo="Reportes por empresa"
                        pill={`${porEmpresa.length} empresa${porEmpresa.length === 1 ? "" : "s"}`}
                        accent={ACENTOS.azul}
                        delay="0.15s"
                      >
                        <Table
                          size="small"
                          rowKey={(r) => r.empresaId || r.nombre}
                          dataSource={porEmpresa}
                          pagination={porEmpresa.length > 10 ? { pageSize: 10 } : false}
                          columns={[
                            { title: "Empresa", dataIndex: "nombre" },
                            { title: "Reportes", dataIndex: "total", width: 100 },
                            { title: "Abiertos", dataIndex: "abiertos", width: 100 },
                            { title: "Atendidos", dataIndex: "atendidos", width: 100 },
                            { title: "Cerrados", dataIndex: "rechazados", width: 110 },
                          ]}
                        />
                      </CardGrafica>
                    </Col>
                  )}

                  <Col xs={24}>
                    <CardGrafica
                      etiqueta="Detalle"
                      titulo="Quién reportó"
                      pill={`${porReportero.length} usuario${porReportero.length === 1 ? "" : "s"}`}
                      accent={ACENTOS.verde}
                      delay="0.18s"
                    >
                      <Table
                        size="small"
                        rowKey={(r) => r.usuarioId || r.nombre}
                        dataSource={porReportero}
                        pagination={porReportero.length > 10 ? { pageSize: 10 } : false}
                        columns={[
                          { title: "Usuario", dataIndex: "nombre" },
                          { title: "Correo", dataIndex: "correo", render: (c) => c || "—" },
                          ...(variasEmpresas
                            ? [{ title: "Empresa", dataIndex: "empresa", render: (e) => e || "—" }]
                            : []),
                          { title: "Reportes", dataIndex: "total", width: 100 },
                        ]}
                      />
                    </CardGrafica>
                  </Col>
                </Row>
              </>
            )}
          </Spin>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
});

export default withRouter(connect(mapStateToProps)(ReporteSoporteEmpresa));
